import { motion } from "framer-motion";
import {
    Music2,
    Megaphone,
    CalendarHeart,
    Film,
    Mic2,
    HeartHandshake,
} from "lucide-react";

const PILLARS_3 = [
    {
        title: "Marketing",
        img: "/assets/marketing-window.jpg",
        copy: "Young Moguls master the fundamentals of graphic design, commercial production, and event planning while building real-world client relations directly on campus.",
    },
    {
        title: "Media",
        img: "/assets/media-window.jpg",
        copy: "Young Moguls develop the skills and pace required for modern digital media by producing high-quality commercials, full-length musical tracks, and engaging short-form content.",
    },
    {
        title: "Mentorship",
        img: "/assets/mentorship-window.jpg",
        copy: "Our experienced mentors build lasting bonds that extend well beyond graduation, keeping alumni connected to the Young Mogul ecosystem for ongoing career opportunities and lifelong professional networks.",
        imgClass: "object-[50%_20%]",
    },
];

const PILLARS_6 = [
    {
        icon: Music2,
        label: "Music Production",
        href: "https://drive.google.com/drive/folders/18uBoGyNBEDEALrHfDlEk9SGR33HPxH87?usp=drive_link",
    },
    {
        icon: Megaphone,
        label: "Media Marketing",
        href: "https://drive.google.com/drive/folders/1G27YcmqgDHUxKpNC0efzHmq6cO57pnrb?usp=drive_link",
    },
    {
        icon: CalendarHeart,
        label: "On-Campus Events",
        href: "https://drive.google.com/drive/folders/1_aNDS2NZ2FNy4bq7mWUsupEdMwKbqKIU?usp=drive_link",
    },
    {
        icon: Film,
        label: "Video Production",
        href: "https://drive.google.com/drive/folders/1hU7gSCBfCLAx36dgDdeO6yCllbO6jkqv?usp=drive_link",
    },
    {
        icon: Mic2,
        label: "Live Performance",
        href: "https://drive.google.com/drive/folders/1AmmVy7BG5o9lwW6TGUGNfK-YDEZpTzvR?usp=drive_link",
    },
    {
        icon: HeartHandshake,
        label: "Community Outreach",
        href: "https://drive.google.com/drive/folders/1whGL0wot97SHvleNrlRv6bGAHnnmEaWu?usp=drive_link",
    },
];

export const About = () => {
    return (
        <section
            id="about"
            data-testid="about-section"
            className="relative py-12 md:py-16 overflow-hidden bg-[#050505]"
        >
            <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-[#D4AF37]/5 rounded-full blur-3xl" />

            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
                {/* Left intro */}
                <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9 }}
                    className="lg:col-span-5 self-start"
                >
                    <span className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
                        / 01 — The Ecosystem
                    </span>
                    <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95]">
                        More Than
                        <br />
                        A Program —
                        <br />
                        <span className="gold-text-gradient">
                            An Ecosystem.
                        </span>
                    </h2>
                    <p className="mt-8 text-white/70 leading-relaxed text-base md:text-lg max-w-md">
                        The Young Mogul Program transforms classrooms into
                        multimedia agencies. Students don't just consume
                        culture — they create it, brand it, and sell it.
                    </p>
                    <p className="mt-4 text-white/60 leading-relaxed text-base md:text-lg max-w-md">
                        We meet our Young Moguls where they are — and walk
                        them into the rooms they belong in. Studios. Stages.
                        Boardrooms. Their own businesses.
                    </p>

                    <div className="mt-[92px] flex flex-wrap items-end gap-8">
                        <div>
                            <div className="font-accent text-6xl text-[#D4AF37] leading-none">
                                400+
                            </div>
                            <div className="text-xs uppercase tracking-[0.25em] text-white/70 mt-2 font-bold">
                                Young Moguls Impacted
                            </div>
                        </div>
                        <div className="lg:ml-8">
                            <div className="font-accent text-6xl text-white leading-none">
                                3,600+
                            </div>
                            <div className="text-xs uppercase tracking-[0.25em] text-white/70 mt-2 font-bold">
                                Students Reached
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Right — 3 pillar cards */}
                <div className="lg:col-span-7 space-y-5">
                    {PILLARS_3.map((p, i) => (
                        <motion.div
                            key={p.title}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{
                                duration: 0.7,
                                delay: i * 0.12,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className="group relative overflow-hidden border border-white/10 hover:border-[#D4AF37]/60 transition-all duration-500 bg-[#0A0A0A]"
                            data-testid={`about-pillar-${p.title.toLowerCase()}`}
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-12">
                                <div className="sm:col-span-5 relative aspect-[4/3] sm:aspect-auto sm:min-h-[208px] overflow-hidden">
                                    <img
                                        src={p.img}
                                        alt={`${p.title} — Young Mogul Program`}
                                        className={`absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ${p.imgClass ?? ""}`}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#0A0A0A] hidden sm:block" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent sm:hidden" />
                                </div>
                                <div className="sm:col-span-7 p-5 md:p-6 flex flex-col justify-start">
                                    <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight leading-none text-white group-hover:text-[#D4AF37] transition-colors duration-500">
                                        {p.title}
                                    </h3>
                                    <div className="mt-3 h-px w-10 bg-[#D4AF37] group-hover:w-16 transition-all duration-500" />
                                    <p className="mt-4 text-white/70 leading-relaxed text-xs md:text-sm">
                                        {p.copy}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Bottom — 6 pillars row, centered icons + labels */}
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-[63px]">
                <div className="text-center mb-8">
                    <span className="text-sm sm:text-base uppercase tracking-[0.4em] text-white font-bold">
                        What Young Moguls Build
                    </span>
                    <p className="mt-3 text-xs sm:text-sm text-white/55 italic tracking-wide">
                        Click any icon below to see their work
                    </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 border-t border-l border-[#D4AF37]/15">
                    {PILLARS_6.map((p) => (
                        <motion.a
                            key={p.label}
                            href={p.href}
                            target="_blank"
                            rel="noreferrer noopener"
                            aria-label={`Open ${p.label} folder in Google Drive`}
                            whileHover={{
                                backgroundColor: "rgba(212,175,55,0.06)",
                            }}
                            className="border-r border-b border-[#D4AF37]/15 p-6 flex flex-col items-center justify-center text-center gap-3 group cursor-pointer min-h-[140px] hover:text-[#D4AF37]"
                            data-testid={`about-pillar6-${p.label.replace(/\s/g, "-").toLowerCase()}`}
                        >
                            <p.icon
                                className="text-[#D4AF37] group-hover:scale-110 transition-transform"
                                size={28}
                            />
                            <span className="text-[11px] uppercase tracking-[0.2em] text-white/75 leading-snug group-hover:text-[#D4AF37] transition-colors">
                                {p.label}
                            </span>
                        </motion.a>
                    ))}
                </div>
            </div>
        </section>
    );
};
